import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useNavigationContext } from '@/hooks/useNavigationHook'
import { useOrder } from '@/providers'

const INACTIVITY_TIME = 30 * 1000 // 30 seconds in milliseconds

export default function useInactivityRedirect(redirectPath?: string) {
    const navigate = useNavigate()
    const { startUrl } = useNavigationContext()
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isPausedRef = useRef(false)
    const [currentCycle, setCurrentCycle] = useState(0)
    const { setShouldShowAreYouThere } = useOrder()

    useEffect(() => {
        const refreshCycle: number =
            Number(localStorage.getItem('refreshCycle')) || 0
        const resetTimer = () => {
            if (isPausedRef.current) return
            if (timer.current) clearTimeout(timer.current)
            timer.current = setTimeout(() => {
                if (isPausedRef.current) return
                // Get current location at timeout time, not when effect was created
                const currentPath = window.location.pathname
                if (
                    !['/register', '/under-maintenance'].includes(
                        currentPath
                    ) && !currentPath.startsWith('/debug/')
                ) {
                    // Only redirect if not already on the redirect path or in register/maintenance pages
                    if (currentPath !== '/buy-voucher-cash') {
                        // hard redirect to avoid issues with service worker that works only on browser reload, redirect, ...

                        // hard redirecty every 15 cycles
                        if (refreshCycle >= 15 && navigator.onLine) {
                            console.log(
                                `HARD redirect to ${redirectPath ?? startUrl ?? '/welcome'} in cycle ${refreshCycle}`
                            )
                            localStorage.setItem('refreshCycle', '0')
                            setCurrentCycle(0)
                            window.location.href =
                                redirectPath ?? startUrl ?? '/welcome'
                        } else {
                            const currentRefreshCycle = refreshCycle + 1
                            console.log(
                                `SOFT redirect to ${redirectPath ?? startUrl ?? '/welcome'} in cycle ${currentRefreshCycle}`
                            )
                            localStorage.setItem(
                                'refreshCycle',
                                currentRefreshCycle.toString()
                            )
                            setCurrentCycle(currentRefreshCycle)
                            navigate(redirectPath ?? startUrl ?? '/welcome', {
                                replace: true
                            })
                        }
                    } else {
                        console.log('Dispatch are-you-still-there event')
                        setShouldShowAreYouThere(true)
                        window.dispatchEvent(new Event('are-you-still-there'))
                        resetTimer()
                    }
                }
            }, INACTIVITY_TIME)
        }

        const pauseInactivity = () => {
            isPausedRef.current = true
            if (timer.current) clearTimeout(timer.current)
            setShouldShowAreYouThere(false)
        }

        const resumeInactivity = () => {
            isPausedRef.current = false
            resetTimer()
        }

        const events = [
            'mousemove',
            'keydown',
            'click',
            'scroll',
            'touchstart',
            'money-added'
        ]

        events.forEach((event) => window.addEventListener(event, resetTimer))
        window.addEventListener('inactivity-pause', pauseInactivity)
        window.addEventListener('inactivity-resume', resumeInactivity)

        resetTimer() // start timer on mount

        return () => {
            events.forEach((event) =>
                window.removeEventListener(event, resetTimer)
            )
            window.removeEventListener('inactivity-pause', pauseInactivity)
            window.removeEventListener('inactivity-resume', resumeInactivity)
            if (timer.current) clearTimeout(timer.current)
        }
    }, [
        navigate,
        redirectPath,
        currentCycle,
        startUrl,
        setShouldShowAreYouThere
    ]) // Include dependencies but not location
}
